import { Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Select, Tab, Tabs, TextField, Typography, BaseTextFieldProps, MenuItem, CircularProgress, Container, Stack, Avatar, Skeleton, Radio, RadioGroup } from '@mui/material'
import React, { useState, useRef, useEffect } from 'react'
import { AmyPorterfield, clientPointWhite, dolbyWhite, ewLogo, fb, foundrWhite, keapWhite, linkdin, Logo, LogoDarkMode, mail, microsoft, dayTrialCloud, susieMoore, teachableWhite, } from '../assets/images'
import { GoogleIcon, LinkedInsmallIcon, MicrosoftIcon } from '../assets/icons'
import Grid from '@mui/material/Grid2'
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import 'react-international-phone/style.css';
import AuthService from '../services/auth.service'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AccountVerification from '../modals/AccountVerification'
import moment from 'moment-timezone';
import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import chargebeeService from '../services/chargebee.service'
import { Applications } from '../types/enums'
import { toast } from 'react-toastify'
import { SocialProvider } from '../types/auth'
import { config } from '../config'
import theme from '../theme'
import { validateEmail, generateSecurePassword } from '../utils/common.util'
import { LeadsquaredService } from '../services/leadsquared/leadsquared.service'
import { ArrowForward, ArrowBack, AutoAwesome, CheckCircle, Bolt } from '@mui/icons-material'
import { 
    SIGNUP_FLOWS,
    logEvent,
    EVENT_TYPES,
    SIGNUP_STEPS
} from '../utils/events.util'


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

// Plan configuration for Sale
interface PlanConfig {
    planId: string;
    planName: string;
    price: number;
    regularMonthlyPrice: number;
    yearlyPrice: number;
    yearlyDiscountedPricePerMonth: number;
    attendees: string; // e.g., "200 Live + 1,000 Automated"
    features: string[];
    savingsBreakdown: {
        regularPrice: string;
        savingsPercentage: string;
        monthlyEquivalent: string;
        note?: string;
    };
    upgradeText?: string; // Text for upgrade section
}

const PLAN_CONFIGS: Record<string, PlanConfig> = {
    'All-In-One-USD-Monthly-200': {
        planId: 'All-In-One-USD-Monthly-200',
        planName: 'Professional',
        price: 70, // Monthly price
        regularMonthlyPrice: 116,
        yearlyPrice: 696,
        yearlyDiscountedPricePerMonth: 58,
        attendees: '200 Live + 1,000 Automated',
        features: [
            'EasySuite CRM (brand-new inbuilt sales CRM — no integrations needed)',
            'Community and monthly workshop access',
            'Complete webinar funnel setup (worth $1,000)',
            'Priority onboarding & support'
        ],
        savingsBreakdown: {
            regularPrice: '$116/month (≈ $1,392/year)',
            savingsPercentage: 'You save $696/year (~50% vs. paying monthly)',
            monthlyEquivalent: '$58/month when paid annually',
        },
        upgradeText: 'Upgrade to Professional Plus — just $492 more for 500 live / 2,500 automated attendees + AI Webinar Builder & Paid Webinars (beta).'
    },
    'All-In-One-USD-Monthly-500': {
        planId: 'All-In-One-USD-Monthly-500',
        planName: 'Professional Plus',
        price: 119, // Monthly price
        regularMonthlyPrice: 198, // 198 * 12
        yearlyPrice: 1188,
        yearlyDiscountedPricePerMonth: 99,
        attendees: '500 Live + 2,500 Automated',
        features: [
            'EasySuite CRM (brand-new inbuilt sales CRM — no integrations needed)',
            'AI Webinar Builder (beta)',
            'Paid Webinars (beta)',
            'Community and monthly workshop access',
            'Complete webinar funnel setup (worth $1,000)',
            'Priority onboarding & support'
        ],
        savingsBreakdown: {
            regularPrice: '$198/month (≈ $2,376/year)',
            savingsPercentage: 'You save $1,188/year (~50% vs. paying monthly)',
            monthlyEquivalent: '$99/month when paid annually'
        }
    },
    'Automated-Gold-USD-Monthly-6000': {
        planId: 'Automated-Gold-USD-Monthly-6000',
        planName: 'Scale',
        price: 145, // Monthly price
        regularMonthlyPrice: 242,
        yearlyPrice: 1452,
        yearlyDiscountedPricePerMonth: 121,
        attendees: '6,000 Automated (Simulive and Evergreen)',
        features: [
            'EasySuite CRM (your inbuilt sales CRM)',
            'AI Webinar Builder (beta)',
            'Paid Webinars (beta)',
            'Community and workshop access',
            'Complete webinar funnel setup (worth $1,000)',
            'Priority onboarding & support'
        ],
        savingsBreakdown: {
            regularPrice: '$242/month (≈ $2,904/year)',
            savingsPercentage: 'You save $1,452/year (~50% vs. paying monthly)',
            monthlyEquivalent: '$121/month when paid annually'
        }
    }
};

const SaleMonthly = () => {
    const navigate = useNavigate();
    const [showPromoField, setShowPromoField] = useState(false);
    const [value, setValue] = React.useState(0);
    const [passwordError, setPasswordError] = useState('');
    const [leadSquaredId, setLeadSquaredId] = useState<string>('');


    const getTimezoneString = () => {
        const timezone = moment.tz.guess() || "UTC";
        const offset = moment.tz(timezone).format("Z");
        const zoneName = moment.tz(timezone).zoneName();
        return `(GMT${offset}),${timezone},${zoneName}`;
    };

    const generateFirstName = (email: string) => {
        const emailParts = email.split("@")[0];
        const name = emailParts
            .split(/[._-]/)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(" ");
        return name;
    };

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        timezone: getTimezoneString(),
        subdomain: ''
    });

    // Auto-generate strong password on component mount
    useEffect(() => {
        const autoGeneratedPassword = generateSecurePassword();
        setFormData(prev => ({
            ...prev,
            password: autoGeneratedPassword
        }));
    }, []);
    const [formErrors, setFormErrors] = useState({
        email: '',
        password: '',
        subdomain: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isCardValid, setIsCardValid] = useState(false);

    const [verificationData, setVerificationData] = useState({
        companyId: '',
        companyName: '',
        infLeadSquaredId: ''
    });

    const [cardToken, setCardToken] = useState<string>("");
    const [cardError, setCardError] = useState<string | null>(null);
    const cardRef = useRef<any>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const [noTrial, setNoTrial] = useState(false);

    const [promoCode, setPromoCode] = useState('');
    const [appliedPromo, setAppliedPromo] = useState<{
        code: string;
        discount: number;
        type: string;
    } | null>(null);
    const [promoError, setPromoError] = useState('');

    const [domainSuggestions, setDomainSuggestions] = useState<string[]>([]);
    const [currentPlanConfig, setCurrentPlanConfig] = useState<PlanConfig | null>(null);

    // Allowed planIds
    const ALLOWED_PLAN_IDS = [
        'All-In-One-USD-Monthly-200',
        'All-In-One-USD-Monthly-500',
        'Automated-Gold-USD-Monthly-6000'
    ];
    const DEFAULT_PLAN_ID = 'All-In-One-USD-Monthly-200';

    const planIdParam = searchParams.get('planId');
    
    // Validate and set default planId if needed
    useEffect(() => {
        if (!planIdParam || !ALLOWED_PLAN_IDS.includes(planIdParam)) {
            setSearchParams((prev) => {
                prev.set("planId", DEFAULT_PLAN_ID);
                return prev;
            }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planIdParam]);

    // Use validated planId or default
    const planId = planIdParam && ALLOWED_PLAN_IDS.includes(planIdParam) ? planIdParam : DEFAULT_PLAN_ID;

    // Helper function to get base planId for API requests (without the number)
    const getBasePlanId = () => {
        // Remove the number at the end to get base planId (e.g., "All-In-One-USD-Monthly-200" -> "All-In-One-USD-Monthly")
        return planId.replace(/-\d+$/, '');
    };

    // Helper function to get planUnits from planId
    const getPlanUnits = () => {
        const match = planId.match(/-(\d+)$/);
        return match ? parseInt(match[1], 10) : 200;
    };


    const handleUpgradeClick = () => {
        // Convert current planId from Monthly to Yearly
        const yearlyPlanId = planId ? planId.replace('Monthly', 'Yearly') : 'All-In-One-USD-Yearly-200';
        // Navigate to annual sale page with converted planId
        navigate(`/black-friday-sale-annual?planId=${yearlyPlanId}&email=${formData.email}`);
    };

    // Store planId in localStorage when component mounts
    useEffect(() => {
        if (planId) {
            localStorage.setItem('planId', planId);
        }
    }, [planId]);


    useEffect(() => {
        // Extract plan details from planId and console log them
        if (planId) {
            // Check if this is one of the three specific Black Friday sale plans
            if (PLAN_CONFIGS[planId]) {
                const config = PLAN_CONFIGS[planId];
                setCurrentPlanConfig(config);
                
                return; // Exit early since we've set everything from config
            }
            
        }
    }, [planId]);


    const validatePassword = (password: string) => {
        const minLength = 8;
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);

        if (password.length < minLength) {
            return 'Password must be at least 8 characters long';
        }
        if (!hasNumber) {
            return 'Password must contain at least one number';
        }
        if (!hasSpecialChar) {
            return 'Password must contain at least one special character';
        }
        if (!hasUppercase) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!hasLowercase) {
            return 'Password must contain at least one lowercase letter';
        }
        return '';
    };



    const validateForm = () => {
        const errors = {
            email: '',
            password: '',
            subdomain: ''
        };
        let isValid = true;

        // Check required fields
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) {
            errors.email = emailValidation.error || "Please enter a valid email";
            isValid = false;
        }

        // Password validation
        if (!formData.password.trim()) {
            errors.password = 'Password is required';
            setPasswordError('Password is required');
            isValid = false;
        } else {
            const passwordValidationError = validatePassword(formData.password);
            if (passwordValidationError) {
                errors.password = passwordValidationError;
                setPasswordError(passwordValidationError);
                isValid = false;
            } else {
                setPasswordError('');
            }
        }

        setFormErrors(errors);
        return isValid;
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // For subdomain field, convert to lowercase
        const processedValue = name === 'subdomain' ? value.toLowerCase() : value;

        // Update the form data with the raw input value
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [name]: ''
        }));

        if (name === 'password') {
            if (!value.trim()) {
                setPasswordError('Password is required');
            } else {
                setPasswordError(validatePassword(value));
            }
        }
    };


    const [verificationDialogOpen, setVerificationDialogOpen] = React.useState(false);

    const handleVerificationDialogOpen = () => {
        setVerificationDialogOpen(true);
    };

    const handleVerificationDialogClose = async () => {
        setVerificationDialogOpen(false);
        const leadData = {
            "leadId": leadSquaredId,
            "fields": [
                { "Attribute": "mx_Plan_Name", "Value": currentPlanConfig?.planName || '' },
                { "Attribute": "mx_Status", "Value": "Pending Verification" }
            ]
        }
        await LeadsquaredService.updateLsField(leadData);
        navigate('/login');
    };

    // Helper function to clear UTM params from localStorage
    const clearUtmParamsFromStorage = () => {
        localStorage.removeItem('utm_source');
        localStorage.removeItem('utm_medium');
        localStorage.removeItem('utm_campaign');
        localStorage.removeItem('utm_content');
        localStorage.removeItem('utm_term');
        localStorage.removeItem('ad_id');
        localStorage.removeItem('fbc_id');
        localStorage.removeItem('h_ad_id');
        localStorage.removeItem('utm_id');
        localStorage.removeItem('gc_id');
        localStorage.removeItem('h_ga_id');
        localStorage.removeItem('h_keyword_id');
        localStorage.removeItem('h_keyword');
        localStorage.removeItem('h_placement');
        localStorage.removeItem('g_special_campaign');
    };

    const handleVerificationDialogProceed = async () => {
        // handleVerificationDialogClose();
        setVerificationDialogOpen(false);
        try {
            await LeadsquaredService.joinActivity({ id: leadSquaredId, activityEvent: 205, activityValue: "Email Verified" });
            const leadData = {
                "leadId": leadSquaredId,
                "fields": [
                    { "Attribute": "mx_Plan_Name", "Value": currentPlanConfig?.planName || '' },
                    { "Attribute": "mx_Status", "Value": "Active" }
                ]
            }
            await LeadsquaredService.updateLsField(leadData);
        } catch (error) {
            console.error('Error updating lead data:', error);
        }

        // Clear UTM parameters from localStorage after successful signup
        clearUtmParamsFromStorage();
  
        // Pass plan data through URL parameters
        const planData = {
            price: currentPlanConfig ? currentPlanConfig.price : 0,
            currency: 'USD',
            coupon: appliedPromo?.code || 'BLACKFRIDAY2025',
            plan_name: currentPlanConfig ? currentPlanConfig.planName : '',
            customer_type: "with_cc"
        };

        // Encode plan data as URL parameters
        const planDataParams = new URLSearchParams({
            ...planData,
            price: planData.price.toString()
        }).toString();

        window.location.href = `/loading-dashboard?ew_email=${formData.email}&planId=${currentPlanConfig ? currentPlanConfig.planId : 'All-In-One-USD-Monthly'}&companyId=${encodeURIComponent(verificationData.companyId)}&${planDataParams}`;
    };



    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        // Only allow changing to the second tab (newValue = 1) if it's done programmatically
        // Users can always go back to the first tab (newValue = 0)
        if (newValue === 0) {
            setValue(newValue);
        }
        // The second tab (newValue = 1) can only be reached through handleNext function
        // which validates the form fields first
    }

    const handleSocialSignIn = (provider: SocialProvider) => {
        try {
            const authUrl = config.sso_auth_url;
            const planIdParam = searchParams.get('planId');

            const utmParams = {
                utm_source: localStorage.getItem('utm_source'),
                utm_medium: localStorage.getItem('utm_medium'),
                utm_campaign: localStorage.getItem('utm_campaign'),
                utm_content: localStorage.getItem('utm_content'),
                utm_term: localStorage.getItem('utm_term'),
                ad_id: localStorage.getItem('ad_id'),
                fbc_id: localStorage.getItem('fbc_id'),
                h_ad_id: localStorage.getItem('h_ad_id'),
                utm_id: localStorage.getItem('utm_id'),
                gc_id: localStorage.getItem('gc_id'),
                h_ga_id: localStorage.getItem('h_ga_id'),
                h_keyword_id: localStorage.getItem('h_keyword_id'),
                h_keyword: localStorage.getItem('h_keyword'),
                h_placement: localStorage.getItem('h_placement'),
                g_special_campaign: localStorage.getItem('g_special_campaign'),
            }

            // Filter out null/undefined/empty values from UTM params
            const cleanUtmParams = Object.entries(utmParams)
                .filter(([_, value]) => value !== null && value !== undefined && value.trim() !== '')
                .reduce((acc, [key, value]) => ({ ...acc, [key]: value?.trim() }), {});

            // Create state parameter with additional metadata
            const stateData = {
                isRegistration: true,
                timestamp: Date.now(),
                flow: 'first-flow',
                ...(planIdParam && { planId: planIdParam }), // Only include planId if it exists
                ...(Object.keys(cleanUtmParams).length > 0 && { utm_params: cleanUtmParams }) // Only include UTM params if they exist
            };

            const baseParams = {
                client_id: config.sso_client_id,
                response_type: 'code',
                scope: 'email openid profile phone',
                redirect_uri: `${window.location.origin}/auth/sso/callback`,
                state: JSON.stringify(stateData),
                prompt: 'select_account'
            };

            const params = new URLSearchParams({
                ...baseParams,
                identity_provider: provider,
            });

            window.location.href = `${authUrl}?${params.toString()}`;
        } catch (error) {
            console.error('Google Sign In Error:', error);
            toast.error('Failed to initialize Google Sign In');
        }
    };

    const handleGoogleSignIn = () => handleSocialSignIn('Google');
    const handleMicrosoftSignIn = () => handleSocialSignIn('Microsoft');
    const handleLinkedinSignIn = () => handleSocialSignIn('Linkedin');

    const handleNext = async () => {
        try {
            setIsLoading(true);

            // Validation and display errors
            const isValid = validateForm();
            if (!isValid) {
                return;
            }

            // Check if email exists
            const emailCheckResponse = await AuthService.checkEmailExists(formData.email);
            if (emailCheckResponse.data?.exists) {
                toast.error("An account with this email already exists. Please use a different email or try logging in.");
                setIsLoading(false);
                return;
            }

            // Auto-generate firstName and lastName from email
            const firstName = generateFirstName(formData.email);
            const lastName = "";

            // Generate subdomain from first name
            const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, '');
            const subdomainResponse = await AuthService.generateSubdomain(baseSubdomain, Applications.EASYWEBINAR);

            // Store the generated subdomain for use in step 2
            setFormData(prev => ({
                ...prev,
                subdomain: subdomainResponse.subdomain
            }));

            // Log signup event before moving to next step
            try {
                const eventData = {
                    step: SIGNUP_STEPS.FORM,
                    password: formData.password,
                    planId: getBasePlanId(),
                    planUnits: getPlanUnits(),
                    signupFlow: SIGNUP_FLOWS.PRICING_PAGE,
                    planName: currentPlanConfig?.planName || '',
                    planPrice: currentPlanConfig?.price || 0,
                    saleType: 'BlackFridaySale'
                }
                
                await logEvent(formData.email, EVENT_TYPES.SIGNUP, eventData);
            } catch (error: any) {
                console.error('Error logging signup event:', error);
                // Don't block the flow if event logging fails
            }

            // If validation and subdomain generation passes, move to second step
            setValue(1);
            try {
                let utm_Data = {
                    utm_source: localStorage.getItem('utm_source'),
                    utm_medium: localStorage.getItem('utm_medium'),
                    utm_campaign: localStorage.getItem('utm_campaign'),
                    utm_content: localStorage.getItem('utm_content'),
                    utm_term: localStorage.getItem('utm_term'),
                    ad_id: localStorage.getItem('ad_id'),
                    fbc_id: localStorage.getItem('fbc_id'),
                    h_ad_id: localStorage.getItem('h_ad_id'),
                    utm_id: localStorage.getItem('utm_id'),
                    gc_id: localStorage.getItem('gc_id'),
                    h_ga_id: localStorage.getItem('h_ga_id'),
                    h_keyword_id: localStorage.getItem('h_keyword_id'),
                    h_keyword: localStorage.getItem('h_keyword'),
                    h_placement: localStorage.getItem('h_placement'),
                    g_special_campaign: localStorage.getItem('g_special_campaign'),
                }

                // Create form data with generated firstName and lastName
                const formDataWithDetails = {
                    firstName,
                    lastName,
                    email: formData.email,
                    password: formData.password,
                    timezone: getTimezoneString(),
                    subdomain: subdomainResponse.subdomain,
                };

                const LsId = await LeadsquaredService.createtLead(formDataWithDetails, "", utm_Data);
                await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 201, activityValue: "Signup Attempt - pricing" });
                const leadData = {
                    "leadId": LsId,
                    "fields": [
                        { "Attribute": "mx_Source_lp", "Value": searchParams.get('Source_IP'), },
                    ]
                }
                await LeadsquaredService.updateLsField(leadData);
                setLeadSquaredId(LsId);
            } catch (error: any) {
                console.error('Error creating lead:', error);
            }
        } catch (error: any) {
            console.error('Error generating subdomain:', error);
            toast.error('Failed to generate subdomain. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGetStarted = async () => {
        // Add domain validation here
        if (!formData.subdomain.trim()) {
            setFormErrors(prev => ({
                ...prev,
                subdomain: 'Domain is required'
            }));
            setDomainSuggestions([]);
            return;
        } else if (!/^[a-zA-Z0-9-]+$/.test(formData.subdomain)) {
            setFormErrors(prev => ({
                ...prev,
                subdomain: 'Domain can only contain letters, numbers, and hyphens'
            }));
            setDomainSuggestions([]);
            return;
        }

        try {
            setIsLoading(true);

            // Check domain availability first
            const domainResponse = await AuthService.checkSubDomainAvailability(
                formData.subdomain,
                Applications.EASYWEBINAR
            );

            if (!domainResponse.available) {
                setFormErrors(prev => ({
                    ...prev,
                    subdomain: 'This domain is already taken. Please try another one.'
                }));
                // Set domain suggestions if they exist
                if (domainResponse.suggestions && domainResponse.suggestions.length > 0) {
                    setDomainSuggestions(domainResponse.suggestions);
                } else {
                    setDomainSuggestions([]);
                }
                setIsLoading(false);
                return;
            }

            // Clear suggestions if domain is available
            setDomainSuggestions([]);

            let cardTokenResult = null;
            const planId = 'lifetime-pro-plan'

            // Only process card if there's a plan and it's not a free trial
            if (planId) {
                // Check if card has any validation errors
                if (cardError) {
                    toast.error('Please fix card validation errors before proceeding');
                    setIsLoading(false);
                    return;
                }

                const result = await cardRef.current.tokenize();

                if (result.error) {
                    setCardError(result.error.message || 'Card tokenization failed');
                    toast.error('Please check your card details.');
                    setIsLoading(false);
                    return;
                }

                cardTokenResult = result.token;
                setCardToken(cardTokenResult);
                setCardError(null);

                if (!cardTokenResult) {
                    setCardError('Please enter valid card details');
                    toast.error('Please enter valid card details');
                    setIsLoading(false);
                    return;
                }
            }

            // Get UTM parameters directly from URL
            const urlParams = new URLSearchParams(window.location.search);

            const utmParams = {
                utm_source: urlParams.get('utm_source'),
                utm_medium: urlParams.get('utm_medium'),
                utm_campaign: urlParams.get('utm_campaign'),
                utm_content: urlParams.get('utm_content'),
                utm_term: urlParams.get('utm_term'),
                ad_id: urlParams.get('ad_id'),
                fbc_id: urlParams.get('fbc_id'),
                h_ad_id: urlParams.get('h_ad_id'),
                utm_id: urlParams.get('utm_id'),
                gc_id: urlParams.get('gc_id'),
                h_ga_id: urlParams.get('h_ga_id'),
                h_keyword_id: urlParams.get('h_keyword_id'),
                h_keyword: urlParams.get('h_keyword'),
                h_placement: urlParams.get('h_placement'),
                g_special_campaign: urlParams.get('g_special_campaign'),
            };

            // Remove null/undefined values from the UTM params object
            const cleanUtmParams = Object.entries(utmParams)
                .filter(([_, value]) => value !== null && value !== undefined)
                .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

            // Check if buyNow parameter is present and true
            const buyNow = urlParams.get('BuyNow') == 'true';


            // Generate firstName and lastName from email
            const firstName = generateFirstName(formData.email);
            const lastName = "";

            const signupData = {
                ...formData,
                firstName,
                lastName,
                phoneNumber: "",
                timezone: getTimezoneString(),
                planId: getBasePlanId(),
                planUnits: getPlanUnits(),
                cardToken: cardTokenResult,
                trialEnd: noTrial ? 0 : undefined,
                couponId: appliedPromo?.code || 'BLACKFRIDAY25M',
                // Include UTM parameters if they exist
                ...(Object.keys(cleanUtmParams).length > 0 && { utm_params: cleanUtmParams })
            };

            const response = await AuthService.signup(signupData);

            setVerificationData({
                companyId: response.data.companyId || '',
                companyName: response.data.companyName || '',
                infLeadSquaredId: response.data.inf_lead_squared_id || ''
            });
            handleVerificationDialogOpen();

            // Clear UTM params on successful signup
            clearUtmParamsFromStorage();
            toast.success('Signup successful! Please verify your account.');
            try {
                await LeadsquaredService.joinActivity({ id: leadSquaredId, activityEvent: 205, activityValue: "Waiting To Confirm Email Validation" });
                
                
                const activityNote = `${currentPlanConfig?.planName || ''} - ${getPlanUnits()} - Black Friday Sale`;
                await LeadsquaredService.joinActivity({ id: leadSquaredId, activityEvent: 205, activityValue: getBasePlanId() || '', activityNote: activityNote });
            
            } catch (error: any) {
                console.error('Error joining activities:', error);
            }
        } catch (error: any) {
            console.error('Signup failed:', error);
            setDomainSuggestions([]);
            // Clear UTM params on signup failure too
            clearUtmParamsFromStorage();
            if (error.name === 'INVALID_CARD') {
                setCardError('Please enter valid card details');
                toast.error('Invalid card details. Please try again.');
            } else {
                toast.error(error.message || 'An error occurred during signup. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const validateCoupon = async (codeToValidate?: string) => {
        const code = codeToValidate || promoCode.trim();
        if (!code) {
            setPromoError('Please enter a promo code');
            toast.error('Please enter a promo code');
            return;
        }

        try {
            const response = await chargebeeService.validateCoupon(
                code,
                getBasePlanId()
            );

            if (response.data.valid) {
                const { coupon } = response.data;
                setAppliedPromo({
                    code: coupon.id,
                    discount: coupon.discountPercentage || coupon.discountAmount,
                    type: coupon.discountType
                });
                setPromoError('');
                setShowPromoField(false);
                if (!codeToValidate) {
                    toast.success('Promo code applied successfully!');
                }
            } else {
                setPromoError(response.data.error || 'Invalid promo code');
                setAppliedPromo(null);
                if (!codeToValidate) {
                    toast.error(response.data.error || 'Invalid promo code');
                }
            }
        } catch (error: any) {
            console.error('Error validating coupon:', error);
            setPromoError('Failed to validate promo code');
            setAppliedPromo(null);
            if (!codeToValidate) {
                toast.error('Failed to validate promo code. Please try again.');
            }
        }
    };

    // Auto-apply BLACKFRIDAY2025 promo code
    useEffect(() => {
        const autoApplyPromo = async () => {
            if (!appliedPromo && planId) {
                await validateCoupon('BLACKFRIDAY25M');
            }
        };
        autoApplyPromo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planId]);


    function captureUTMParams() {
        const urlParams = new URLSearchParams(window.location.search);

        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');
        const utmContent = urlParams.get('utm_content');
        const utmTerm = urlParams.get('utm_term');
        const email = urlParams.get('email');
        const ad_id = urlParams.get('ad_id');
        const fbc_id = urlParams.get('fbc_id');
        const h_ad_id = urlParams.get('h_ad_id');
        const utm_id = urlParams.get('utm_id');
        const gc_id = urlParams.get('gc_id');
        const h_ga_id = urlParams.get('h_ga_id');
        const h_keyword_id = urlParams.get('h_keyword_id');
        const h_keyword = urlParams.get('h_keyword');
        const h_placement = urlParams.get('h_placement');
        const g_special_campaign = urlParams.get('g_special_campaign');

        if (utmSource) localStorage.setItem('utm_source', utmSource);
        if (utmMedium) localStorage.setItem('utm_medium', utmMedium);
        if (utmCampaign) localStorage.setItem('utm_campaign', utmCampaign);
        if (utmContent) localStorage.setItem('utm_content', utmContent);
        if (utmTerm) localStorage.setItem('utm_term', utmTerm);
        
        if (ad_id) localStorage.setItem('ad_id', ad_id);
        if (fbc_id) localStorage.setItem('fbc_id', fbc_id);
        if (h_ad_id) localStorage.setItem('h_ad_id', h_ad_id);
        if (utm_id) localStorage.setItem('utm_id', utm_id);
        if (gc_id) localStorage.setItem('gc_id', gc_id);
        if (h_ga_id) localStorage.setItem('h_ga_id', h_ga_id);
        if (h_keyword_id) localStorage.setItem('h_keyword_id', h_keyword_id);
        if (h_keyword) localStorage.setItem('h_keyword', h_keyword);
        if (h_placement) localStorage.setItem('h_placement', h_placement);
        if (g_special_campaign) localStorage.setItem('g_special_campaign', g_special_campaign);
        // Set email from URL parameter if present
        if (email) {
            setFormData(prev => ({
                ...prev,
                email: email
            }));
        }
    }

    // Add useEffect for capturing UTM parameters
    useEffect(() => {
        captureUTMParams();
    }, []);


    // Check for BuyNow parameter on page load
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const buyNow = urlParams.get('BuyNow') === 'true';
        setNoTrial(buyNow);
    }, []);

    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    width: "100vw",
                    background: `
                    linear-gradient(180deg, #010007 26.92%, #AC8DFF 100%),
                    linear-gradient(180deg, #FFFFEF 0%, #FFF 100%)
            `,
                    color: "#1f2937",
                    fontFamily: "Inter",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: { xs: "16px 24px", sm: "24px 32px" },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                            src={LogoDarkMode}
                            alt="logo"
                            style={{ height: 24, cursor: "pointer", maxWidth: "100%" }}
                            onClick={() => window.open("https://easywebinar.com/", "_blank")}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        size="small"
                        onClick={() => navigate("/login")}
                        sx={{
                            bgcolor: "#6066E9",
                            color: "#fff",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "#4B4EB5" },
                        }}
                    >
                        Login
                    </Button>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: {
                            xs: "calc(100vh - 120px)",
                            sm: "calc(100vh - 100px)",
                            md: "calc(100vh - 80px)",
                        },
                        p: { xs: 1, sm: 2 },
                        pt: { xs: 1, sm: 2, md: 3 },
                    }}
                >
                    {/* Main container with border - responsive sizing */}
                    <Box
                        sx={{
                            width: { xs: "100%", sm: "90%", md: "1200px", lg: "1400px" },
                            maxWidth: { xs: "600px", md: "none" },
                            height: { xs: "auto" },
                            border: "1.5px solid #e5e7eb",
                            borderRadius: "18px",
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            bgcolor: "#ffffff",
                            boxShadow: "0 4px 32px 0 rgba(0,0,0,0.1)",
                            overflow: "hidden",
                        }}
                    >
                        {/* Left side - Promotional Section */}
                        <Box
                            sx={{
                                width: "50%",
                                bgcolor: "#f8fafc",
                                p: { xs: 3, sm: 4, md: 6 },
                                display: { xs: "none", md: "flex" },
                                flexDirection: "column",
                                justifyContent: "center",
                                borderRadius: { xs: "18px", md: "18px 0 0 18px" },
                                minHeight: { xs: "500px", md: "auto" },
                            }}
                        >
                            {value === 0 ? (
                                <>
                                    {/* Main heading with statistics */}
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "22px" },
                                            textAlign: "center",
                                            mb: 10,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        EasyWebinar has powered{" "}
                                        <Box component="span" sx={{ fontWeight: 700, }}>
                                            200K+
                                        </Box> <br />
                                        webinars with over{" "}
                                        <Box component="span" sx={{ fontWeight: 700, }}>
                                            69M+
                                        </Box>{" "}
                                        registrations and counting...
                                    </Typography>

                                    {/* Testimonial Block */}
                                    <Box sx={{ mb: 10 }}>
                                        <Stack direction="row" spacing={3} alignItems="center" justifyContent="center" sx={{ maxWidth: 477, margin: "auto" }}>
                                            <Box
                                                component="img"
                                                src={AmyPorterfield}
                                                alt="Amy Porterfield"
                                                sx={{
                                                    width: { xs: 100, sm: 150, md: 180 },
                                                    height: { xs: 100, sm: 150, md: 180 },
                                                    borderRadius: "8px",
                                                    objectFit: "cover",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Stack direction="row" spacing={0.5} mb={1}>
                                                    {[...Array(5)].map((_, i) => (
                                                        <StarRoundedIcon key={i} sx={{ color: '#FDBF2F', fontSize: "16px" }} />
                                                    ))}
                                                </Stack>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: "#374151",
                                                        fontSize: "16px",
                                                        lineHeight: 1.5,
                                                        mb: 1,
                                                    }}
                                                >
                                                    "I personally use EasyWebinar and they are the best. I moved from using other platforms and nothing compares to their reliability and customer support!"
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette.text.secondary,
                                                        fontWeight: 600,
                                                        fontSize: "16px",
                                                    }}
                                                >
                                                    Amy Porterfield
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.palette.text.tertiary,
                                                        fontSize: "14px",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    Online Marketing Expert
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    {/* Shield Badges */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: 2,
                                            mb: 4,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {/* G3 Icons Group Image */}
                                        <Box
                                            component="img"
                                            src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g3-icons-3-group.png"
                                            srcSet="
                                                https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g3-icons-3-group.png 1x,
                                                https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g3-icons-3-group-2x.png 2x,
                                                https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g3-icons-3-group-3x.png 3x
                                            "
                                            alt="G3 Icons Group"
                                            sx={{
                                                maxWidth: "100%",
                                                height: "auto",
                                                borderRadius: "8px",
                                            }}
                                        />
                                    </Box>

                                    {/* Company Logos */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: 3,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={keapWhite}
                                            alt="keap"
                                            sx={{
                                                filter: "brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)",
                                                opacity: 0.5,
                                            }}
                                        />
                                        <Box
                                            component="img"
                                            src={teachableWhite}
                                            alt="teachable"
                                            sx={{
                                                filter: "brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)",
                                                opacity: 0.5,
                                            }}
                                        />
                                        <Box
                                            component="img"
                                            src={foundrWhite}
                                            alt="foundr"
                                            sx={{
                                                filter: "brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)",
                                                opacity: 0.5,
                                            }}
                                        />
                                        <Box
                                            component="img"
                                            src={clientPointWhite}
                                            alt="clientPoint"
                                            sx={{
                                                filter: "brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)",
                                                opacity: 0.5,
                                            }}
                                        />
                                    </Box>
                                </>
                            ) : value === 1 ? (
                                <>
                                    {/* Back Link */}
                                    <Box sx={{ mb: 3 }}>
                                        <Button
                                            startIcon={<ArrowBack />}
                                            onClick={() => setValue(0)}
                                            sx={{
                                                color: '#374151',
                                                textTransform: 'none',
                                                fontSize: '16px',
                                                fontWeight: 500,
                                                p: 0,
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    bgcolor: 'transparent',
                                                    textDecoration: 'underline',
                                                    boxShadow: 'none !important',
                                                }
                                            }}
                                        >
                                            Back
                                        </Button>
                                    </Box>

                                    {/* Black Friday Banner */}
                                    <Box
                                        sx={{
                                            bgcolor: '#1f2937',
                                            borderRadius: '8px',
                                            p: { xs: '12px', md: '16px' },
                                            mb: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <AutoAwesome sx={{ color: '#ffffff', fontSize: '20px' }} />
                                        <Typography
                                            sx={{
                                                color: '#ffffff',
                                                fontSize: { xs: '14px', md: '16px' },
                                                fontWeight: 700,
                                            }}
                                        >
                                            BLACK FRIDAY ONLY - ENDS SOON!
                                        </Typography>
                                    </Box>

                                    {/* Save $696/year - Large Green Text */}
                                    <Typography
                                        sx={{
                                            color: '#22B060',
                                            fontSize: { xs: '28px', md: '32px' },
                                            fontWeight: 700,
                                            mb: 1
                                        }}
                                    >
                                        Save ${currentPlanConfig ? currentPlanConfig.yearlyPrice : '696'}/year
                                    </Typography>

                                    {/* Lock in 50% savings FOREVER */}
                                    <Typography
                                        sx={{
                                            color: '#22B060',
                                            fontSize: { xs: '16px', md: '18px' },
                                            fontWeight: 600,
                                            mb: 3
                                        }}
                                    >
                                        Lock in 50% savings FOREVER
                                    </Typography>

                                    {/* Explanation Paragraph */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#374151',
                                            fontSize: { xs: '14px', md: '16px' },
                                            lineHeight: 1.6,
                                            mb: 4
                                        }}
                                    >
                                        Just a heads-up — this Black Friday discount is a one-time deal. After the promo, your plan will go back to ${currentPlanConfig ? currentPlanConfig.regularMonthlyPrice : '116'}/month. But if you switch to annual now, you'll lock in ${currentPlanConfig ? currentPlanConfig.yearlyDiscountedPricePerMonth : '70'}/month forever at 50% off (billed at ${currentPlanConfig ? currentPlanConfig.yearlyPrice : '696'}/year).
                                    </Typography>

                                    {/* Black Friday Bonuses Heading */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <AutoAwesome sx={{ color: '#178EFC', fontSize: '20px' }} />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: '#1f2937',
                                                fontSize: { xs: '18px', md: '20px' },
                                                fontWeight: 700,
                                            }}
                                        >
                                            Black Friday bonuses are included with the annual plan when you upgrade
                                        </Typography>
                                    </Box>

                                    {/* Features List */}
                                    <Box sx={{ mb: 4 }}>
                                        {[
                                            { text: 'EasySuite CRM (your inbuilt sales CRM)', badge: 'NEW' },
                                            { text: 'AI Webinar Builder', badge: 'NEW - BETA' },
                                            { text: 'Paid Webinars', badge: 'NEW' },
                                            { text: 'Community and workshop access', badge: null },
                                            { text: 'Complete Webinar Funnel Setup', badge: '(WORTH $1000)' }
                                        ].map((feature, index) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                <CheckCircle sx={{ color: '#000000', fontSize: '20px', mr: 1.5, mt: 0.5, flexShrink: 0 }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: '#1f2937',
                                                                fontSize: { xs: '15px', md: '16px' },
                                                                fontWeight: 500,
                                                                lineHeight: 1.5,
                                                            }}
                                                        >
                                                            {feature.text}
                                                        </Typography>
                                                        {feature.badge && (
                                                            <Box
                                                                sx={{
                                                                    bgcolor: '#1f2937',
                                                                    color: '#ffffff',
                                                                    px: 1,
                                                                    py: 0.5,
                                                                    borderRadius: '4px',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    lineHeight: 1
                                                                }}
                                                            >
                                                                {feature.badge}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Lock in Annual Rate Button */}
                                    <Button
                                        variant="contained"
                                        startIcon={<AutoAwesome sx={{ color: '#ffffff' }} />}
                                        onClick={handleUpgradeClick}
                                        sx={{
                                            bgcolor: '#22B060',
                                            color: '#ffffff',
                                            textTransform: 'none',
                                            fontSize: { xs: '16px', md: '18px' },
                                            fontWeight: 700,
                                            py: 2,
                                            px: 3,
                                            borderRadius: '8px',
                                            width: '100%',
                                            '&:hover': {
                                                bgcolor: '#1a9a4a'
                                            }
                                        }}
                                    >
                                        Lock in ${currentPlanConfig ? currentPlanConfig.yearlyDiscountedPricePerMonth : '58'}/month with annual. Save ${currentPlanConfig ? currentPlanConfig.yearlyPrice : '696'}/year.
                                    </Button>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'left' }}
                                    margin="auto">
                                    {currentPlanConfig ? (
                                        <>
                                            {/* Plan Name */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: '16px' }}>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '20px', sm: '24px', md: '28px' },
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {currentPlanConfig.planName} (Annual Access)
                                                </Typography>
                                            </Box>

                                            {/* Attendees Badge */}
                                            <Box
                                                sx={{
                                                    bgcolor: '#EDF6FF',
                                                    borderRadius: '12px',
                                                    p: '12px',
                                                    mb: '24px',
                                                    border: '1px solid #B3D9FF',
                                                    width: 'fit-content',
                                                    maxWidth: '100%',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                                                    <Box
                                                        sx={{
                                                            width: '24px',
                                                            height: '24px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mr: '8px',
                                                        }}
                                                    >
                                                        <Box
                                                            component="svg"
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 20 20"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <circle cx="10" cy="7.5" r="2.5" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
                                                            <path d="M5.25 13.0625C6.5 11.9062 8.20507 11.25 10 11.25C11.7949 11.25 13.5 11.9062 14.75 13.0625C16 14.2188 16.75 15.8125 16.75 17.5H3.25C3.25 15.8125 4 14.2188 5.25 13.0625Z" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
                                                        </Box>
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            color: '#6B7280',
                                                            fontSize: '16px',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        Attendees
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px' }}>
                                                    {(() => {
                                                        const attendeesText = currentPlanConfig.attendees;
                                                        // Parse the attendees string (e.g., "200 Live + 1,000 Automated" or "6,000 Automated")
                                                        const parts = attendeesText.includes(' + ') 
                                                            ? attendeesText.split(' + ') 
                                                            : [attendeesText];
                                                        
                                                        return parts.map((part, index) => {
                                                            const trimmedPart = part.trim();
                                                            const match = trimmedPart.match(/^([\d,]+)\s+(.+)$/);
                                                            if (match) {
                                                                const [, number, label] = match;
                                                                const isLive = label.toLowerCase().includes('live') && !label.toLowerCase().includes('automated');
                                                                const isAutomated = label.toLowerCase().includes('automated');
                                                                
                                                                return (
                                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                                                        <Typography
                                                                            sx={{
                                                                                color: isLive ? '#178EFC' : '#7C3AED',
                                                                                fontSize: '18px',
                                                                                fontWeight: 700,
                                                                                lineHeight: 1
                                                                            }}
                                                                        >
                                                                            {number}
                                                                        </Typography>
                                                                        <Typography
                                                                            sx={{
                                                                                color: isLive ? '#178EFC' : '#7C3AED',
                                                                                fontSize: '18px',
                                                                                fontWeight: 700,
                                                                                lineHeight: 1
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </Typography>
                                                                        {index < parts.length - 1 && (
                                                                            <Typography
                                                                                sx={{
                                                                                    color: '#6B7280',
                                                                                    fontSize: '14px',
                                                                                    fontWeight: 500,
                                                                                    mx: '4px',
                                                                                    lineHeight: 1
                                                                                }}
                                                                            >
                                                                                +
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                );
                                                            }
                                                            return null;
                                                        });
                                                    })()}
                                                </Box>
                                            </Box>

                                            {/* Black Friday Exclusive Bonuses Heading */}
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: '#1f2937',
                                                    fontSize: { xs: '18px', md: '20px' },
                                                    fontWeight: 700,
                                                    mb: { xs: '16px', md: '20px' },
                                                    mt: '24px'
                                                }}
                                            >
                                                Black Friday Exclusive Bonuses
                                            </Typography>

                                            {/* Features list */}
                                            <Box sx={{ mb: { xs: "24px", md: "32px" } }}>
                                                {currentPlanConfig.features.map((feature, index) => (
                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                        <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.8, mr: '12px', mt: '2px' }}>•</Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: '#1f2937',
                                                                fontSize: { xs: '16px', xl: '18px' },
                                                                fontWeight: 500,
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {feature}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    ) : (
                                    <Box sx={{ textAlign: 'left' }}
                                        margin="auto">
                                        {/* Main heading */}
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                color: '#1f2937',
                                                fontSize: { xs: '20px', sm: '24px', md: '28px' },
                                                fontWeight: 700,
                                                mb: '8px',
                                                textAlign: 'left'
                                            }}
                                        >
                                            You're getting Lifetime Access
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: '#7C7D7E',
                                                fontSize: { xs: '14px', sm: '16px', md: '18px' },
                                                fontWeight: 600,
                                                lineHeight: 1.2,
                                                textAlign: 'left',
                                                mb: '30px'
                                            }}
                                        >
                                            Full control, one-time payment. All tools to grow, connect, and sell on autopilot.
                                        </Typography>
                                        {/* Features list */}
                                        <Box sx={{ mb: { xs: "16px", md: "20px" } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.8, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '16px', xl: '18px' },
                                                        fontWeight: 600,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    Host up to 500 live attendees + 3000 automated webinar attendees a month.
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.8, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '16px', xl: '18px' },
                                                        fontWeight: 600,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    EasySuite CRM + EasyCast included
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.8, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '16px', xl: '18px' },
                                                        fontWeight: 600,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    AI Webinar Generator (Beta)
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.8, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '16px', xl: '18px' },
                                                        fontWeight: 600,
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    Priority onboarding & support
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    )}

                                    {/* Savings Breakdown Section */}
                                    <Box
                                        sx={{
                                            backgroundColor: '#F5F5F5',
                                            borderRadius: '8px',
                                            padding: { xs: '20px', md: '24px' },
                                            mb: { xs: '24px', md: '32px' },
                                            mt: { xs: '24px', md: '32px' }
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: '#1f2937',
                                                fontSize: { xs: '18px', md: '20px' },
                                                fontWeight: 700,
                                                mb: { xs: '16px', md: '20px' },
                                                textAlign: 'left'
                                            }}
                                        >
                                            Savings Breakdown
                                        </Typography>
                                        <Box>
                                            {currentPlanConfig ? (
                                                <>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                        <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: '#1f2937',
                                                                fontSize: { xs: '14px', sm: '16px' },
                                                                fontWeight: 400,
                                                                lineHeight: 1.0,
                                                            }}
                                                        >
                                                            Regular price: {currentPlanConfig.savingsBreakdown.regularPrice}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                        <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: '#1f2937',
                                                                fontSize: { xs: '14px', sm: '16px' },
                                                                fontWeight: 400,
                                                                lineHeight: 1.0,
                                                            }}
                                                        >
                                                            {currentPlanConfig.savingsBreakdown.savingsPercentage}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                        <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: '#1f2937',
                                                                fontSize: { xs: '14px', sm: '16px' },
                                                                fontWeight: 400,
                                                                lineHeight: 1.0,
                                                            }}
                                                        >
                                                            Equivalent to {currentPlanConfig.savingsBreakdown.monthlyEquivalent}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '14px', sm: '16px' },
                                                        fontWeight: 400,
                                                        lineHeight: 1.0,
                                                    }}
                                                >
                                                    Regular price: $198/month (≈ $2,376/year)
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '14px', sm: '16px' },
                                                        fontWeight: 400,
                                                        lineHeight: 1.0,
                                                    }}
                                                >
                                                    Lifetime deal: $3,995 one-time
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '14px', sm: '16px' },
                                                        fontWeight: 400,
                                                        lineHeight: 1.0,
                                                    }}
                                                >
                                                    Based on 8-year use, you save ~79% vs. paying annually
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: "12px", md: "16px" } }}>
                                                <Box sx={{ color: '#000000', fontSize: '20px', lineHeight: 0.6, mr: '12px', mt: '2px' }}>•</Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '14px', sm: '16px' },
                                                        fontWeight: 400,
                                                        lineHeight: 1.0,
                                                    }}
                                                >
                                                    Equivalent to ~$41.60/month over 8 years
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#7C7D7E',
                                                        fontSize: { xs: '12px', sm: '14px' },
                                                        fontWeight: 400,
                                                        lineHeight: 1.0,
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    (Based on a minimum 8-year lifespan of EasyWebinar V2 — V1 has been active for over 10 years.)
                                                </Typography>
                                            </Box>
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Want more capacity and features? Section */}
                                    {currentPlanConfig && currentPlanConfig.upgradeText && (
                                    <Box
                                        sx={{
                                                border: '1px solid #178EFC',
                                                borderRadius: '8px',
                                                p: { xs: '16px', md: '20px' },
                                                mt: { xs: '24px', md: '32px' },
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bgcolor: '#F0F8FF',
                                                    borderColor: '#006dd1'
                                                }
                                            }}
                                            onClick={handleUpgradeClick}
                                    >
                                        <Typography
                                                variant="body1"
                                            sx={{
                                                    color: '#1f2937',
                                                    fontSize: { xs: '14px', sm: '16px' },
                                                    fontWeight: 600,
                                                    mb: '8px'
                                                }}
                                            >
                                                Want more capacity and features?
                                        </Typography>
                                            <Typography
                                                variant="body2"
                                            sx={{
                                                    color: '#6B7280',
                                                    fontSize: { xs: '13px', sm: '14px' },
                                                    fontWeight: 400,
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {currentPlanConfig.upgradeText}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Right side - Form Section */}
                        <Box
                            sx={{
                                width: { xs: "100%", md: "50%" },
                                bgcolor: "#ffffff",
                                p: { xs: 3, sm: 4, md: 6 },
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                borderRadius: { xs: "18px", md: "0 18px 18px 0" },
                                minHeight: { xs: "500px", md: "auto" },
                            }}
                        >
                            {value === 1 ? (
                                noTrial ? (
                                    <>
                                        <Box sx={{ position: "relative", textAlign: 'center' }}>
                                            <Typography
                                                color="#333333"
                                                fontWeight="700"
                                                fontSize={{ xs: "24px", xl: "30px" }}
                                                lineHeight="normal"
                                                mb="12px"
                                                display="inline-block"
                                                textAlign="center"
                                                sx={{ position: "relative" }}
                                            >
                                                Complete your purchase
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Box sx={{ position: "relative", textAlign: 'center' }}>
                                            <Typography
                                                color="#333333"
                                                fontWeight="700"
                                                fontSize={{ xs: "24px", xl: "30px" }}
                                                lineHeight="normal"
                                                mb="12px"
                                                display="inline-block"
                                                textAlign="center"
                                                sx={{ position: "relative" }}
                                            >
                                                Start your 7-day free trial
                                            </Typography>
                                        </Box>
                                        <Box display="flex" gap="12px" mb="24px" flexWrap="wrap" justifyContent="center" alignItems="center">
                                            <>
                                                <Typography
                                                    color="#666666"
                                                    fontSize="14px"
                                                >
                                                    You’ll only be charged (with the discount) once your trial ends.
                                                </Typography>
                                            </>
                                        </Box>
                                    </>
                                )
                            ) : (
                            <>
                                <Box sx={{ position: "relative", textAlign: 'center' }}>
                                    <Typography
                                        color="#333333"
                                        fontWeight="700"
                                        fontSize={{ xs: "24px", xl: "30px" }}
                                        lineHeight="normal"
                                        mb="8px"
                                        display="inline-block"
                                        textAlign="center"
                                        sx={{ position: "relative" }}
                                    >
                                        Create your account
                                    </Typography>
                                </Box>
                                <Box display="flex" gap="12px" mb="24px" flexWrap="wrap" justifyContent="center" alignItems="center">
                                    <>
                                        <Typography
                                            color="#666666"
                                            fontSize="14px"
                                        >
                                            Get full access
                                        </Typography>
                                        <Box borderRadius="50%" width="4px" height="4px" sx={{ background: "#666" }}></Box>
                                        <Typography
                                            color="#666666"
                                            fontSize="14px"
                                        >
                                            Cancel anytime
                                        </Typography>
                                        <Box borderRadius="50%" width="4px" height="4px" sx={{ background: "#666" }}></Box>
                                        <Typography
                                            color="#666666"
                                            fontSize="14px"
                                        >
                                            No hidden fees
                                        </Typography>
                                    </>
                                </Box>
                                </>
                            )}

                            <Box sx={{ width: '100%' }} className="umbrella-signIn-tabs">
                                <Box sx={{ display: "flex", justifyContent: "center", height: "4px" }}>
                                    <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                                        <Tab {...a11yProps(0)} sx={{
                                            '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
                                                color: '#178EFC !important',
                                            },
                                            '&.MuiTabs-indicator': {
                                                backgroundColor: '#178EFC !important',
                                            },
                                        }} />
                                        <Tab {...a11yProps(1)} disabled={value !== 1} />
                                    </Tabs>
                                </Box>
                                <CustomTabPanel value={value} index={0}>
                                    {/* -----------commented below code as per the requirement---------- */}
                                    {/* <Box width="100%" textAlign="center" mb="24px">
                                        <Box display="flex" mb="14px" justifyContent="center" flexWrap="wrap" gap="12px">
                                            <Box height={{ xs: "38px", sm: "42px" }} minWidth={{ xs: "fit-content", sm: "138px" }} px={{ xs: "12px", sm: "20px" }} borderRadius="2.747px" display="flex" alignItems="center" justifyContent="center" border="1px solid #D0D5DD" sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }} onClick={handleGoogleSignIn}>
                                                <Box mr={{ xs: "8px" }} display="flex"><GoogleIcon /></Box>
                                                <Typography variant="body1" color="#0C141D">Google</Typography>
                                            </Box>
                                            <Box height={{ xs: "38px", sm: "42px" }} minWidth={{ xs: "fit-content", sm: "138px" }} px={{ xs: "12px", sm: "20px" }} borderRadius="2.747px" display="flex" alignItems="center" justifyContent="center" border="1px solid #D0D5DD" sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }} onClick={handleLinkedinSignIn}>
                                                <Box mr={{ xs: "8px" }} display="flex"><LinkedInsmallIcon /></Box>
                                                <Typography variant="body1" color="#0C141D">Linkedin</Typography>
                                            </Box> */}
                                    {/* <Box height={{ xs: "38px", sm: "42px" }} minWidth={{ xs: "fit-content", sm: "138px" }} px={{ xs: "12px", sm: "20px" }} borderRadius="2.747px" display="flex" alignItems="center" justifyContent="center" border="1px solid #D0D5DD" sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }}>
                            <Box mr={{ xs: "8px" }}><MicrosoftIcon /></Box>
                            <Typography variant="body1" color="#0C141D">Microsoft</Typography>
                        </Box> */}
                                    {/* </Box>
                                        <Box borderBottom="1px solid #D9D9D9" height="1px" position="relative" width={{ xs: "100%", sm: "76%" }} margin="24px auto 27px" > <Typography variant="body2" color="#667085" bgcolor="#ffffff" position="absolute" left="50%" sx={{ transform: "translate(-50%, -50%)" }} p="0 6px" whiteSpace="nowrap">Or  continue with email</Typography></Box>
                                    </Box> */}
                                    {/* -----------commented below code as per the requirement---------- */}

                                    <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }} mb={{ xs: "24px", sm: "48px" }}>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                variant="outlined"
                                                label="Work email"
                                                name="email"
                                                placeholder="you@company.com"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                error={!!formErrors.email}
                                                helperText={formErrors.email}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: '3px',
                                                        borderColor: '#666666',
                                                    },

                                                    " .MuiInputLabel-root.Mui-focused": {
                                                        color: "inherit",
                                                    },

                                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: '#333333',
                                                        border: '1px solid'
                                                    },

                                                    "& .MuiOutlinedInput-input": {
                                                        padding: '14px 16px',
                                                        background: '#ffffff',
                                                        borderRadius: '8px'
                                                    },
                                                    "&.MuiInputBase-input::placeholder": {
                                                        color: '#333333',
                                                        fontSize: '16px',
                                                        opacity: '0.8'
                                                    },
                                                }}
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        disabled={isLoading}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textTransform: "unset",
                                            width: "100%",
                                            height: "51px",
                                            gap: "8px",
                                            fontSize: "16px",
                                            fontWeight: "500",
                                            margin: "auto",
                                            bgcolor: "#0382F7 !important",
                                            "&:hover": { bgcolor: "#006dd1 !important" },
                                            "&.Mui-disabled": {
                                                bgcolor: "#178EFC !important",
                                                opacity: 0.7
                                            }
                                        }}
                                        onClick={handleNext}
                                    >
                                        {isLoading ? (
                                            <CircularProgress size={24} sx={{ color: 'white' }} />
                                        ) : (
                                            'Continue to setup'
                                        )}
                                    </Button>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            mt: 2,
                                            mb: 2
                                        }}
                                    >
                                        {/* {!noTrial && <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            100% Free Trial • Cancel Anytime • No Hidden Fees
                                        </Typography> } */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#374151',
                                                fontSize: '10px',
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                                mt: 2,
                                            }}
                                        >
                                            By starting your {noTrial ? 'plan' : 'trial'}, you agree to our <a href="https://easywebinar.com/terms-conditions/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit !important", textDecoration: "underline" }}>Terms of Service</a> and <a href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit !important", textDecoration: "underline" }}>Privacy Policy</a>
                                        </Typography>
                                    </Box>

                                </CustomTabPanel>

                                <CustomTabPanel value={value} index={1}>
                                    {/* Plan Details Card */}
                                    <Box
                                        sx={{
                                            bgcolor: '#EDF6FF',
                                            borderRadius: '8px',
                                            p: { xs: '16px', md: '20px' },
                                            mb: 3,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                            gap: 2
                                        }}
                                    >
                                        {/* Left Side - Plan Name and Attendees */}
                                        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                                            <Typography
                                                sx={{
                                                    color: '#1f2937',
                                                    fontSize: { xs: '16px', md: '18px' },
                                                    fontWeight: 700,
                                                    mb: 1.5
                                                }}
                                            >
                                                {currentPlanConfig ? `${currentPlanConfig.planName} (Monthly Access)` : 'Professional (Monthly Access)'}
                                            </Typography>
                                            
                                            {/* Attendees Section */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Box
                                                    sx={{
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mr: '4px'
                                                    }}
                                                >
                                                    <Box
                                                        component="svg"
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 20 20"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <circle cx="10" cy="7.5" r="2.5" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
                                                        <path d="M5.25 13.0625C6.5 11.9062 8.20507 11.25 10 11.25C11.7949 11.25 13.5 11.9062 14.75 13.0625C16 14.2188 16.75 15.8125 16.75 17.5H3.25C3.25 15.8125 4 14.2188 5.25 13.0625Z" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
                                                    </Box>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: '#6B7280',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        mr: 0.5,
                                                        mt: '6px'
                                                    }}
                                                >
                                                    Attendees
                                                </Typography>
                                                {currentPlanConfig && currentPlanConfig.attendees ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: '6px' }}>
                                                        {(() => {
                                                            const attendeesText = currentPlanConfig.attendees;
                                                            const parts = attendeesText.includes(' + ') 
                                                                ? attendeesText.split(' + ') 
                                                                : [attendeesText];
                                                            
                                                            return parts.map((part, index) => {
                                                                const trimmedPart = part.trim();
                                                                const match = trimmedPart.match(/^([\d,]+)\s+(.+)$/);
                                                                if (match) {
                                                                    const [, number, label] = match;
                                                                    const isLive = label.toLowerCase().includes('live') && !label.toLowerCase().includes('automated');
                                                                    const isAutomated = label.toLowerCase().includes('automated');
                                                                    
                                                                    return (
                                                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <Typography
                                                                                sx={{
                                                                                    color: isLive ? '#178EFC' : '#7C3AED',
                                                                                    fontSize: '14px',
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {number} {label}
                                                                            </Typography>
                                                                            {index < parts.length - 1 && (
                                                                                <Typography
                                                                                    sx={{
                                                                                        color: '#6B7280',
                                                                                        fontSize: '14px',
                                                                                        fontWeight: 500,
                                                                                    }}
                                                                                >
                                                                                    +
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                }
                                                                return null;
                                                            });
                                                        })()}
                                                        <Typography
                                                            sx={{
                                                                color: '#6B7280',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                ml: 0.5
                                                            }}
                                                        >
                                                            /month
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography
                                                            sx={{
                                                                color: '#178EFC',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            200 Live
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#6B7280',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                mx: '4px',
                                                            }}
                                                        >
                                                            +
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#7C3AED',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            1,000 Automated
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#6B7280',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                ml: 0.5
                                                            }}
                                                        >
                                                            /month
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Right Side - Pricing */}
                                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: { xs: '100%', sm: 'auto' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, mb: 0.5 }}>
                                                <Typography
                                                    sx={{
                                                        color: '#6B7280',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        textDecoration: 'line-through'
                                                    }}
                                                >
                                                    ${currentPlanConfig ? currentPlanConfig.regularMonthlyPrice : '116'}/month
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: '#6B7280',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    40% off
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                                                <Typography
                                                    sx={{
                                                        color: '#1f2937',
                                                        fontSize: { xs: '24px', md: '28px' },
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    ${currentPlanConfig ? currentPlanConfig.price : '70'}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: '#6B7280',
                                                        fontSize: '16px',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    /month
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <>
                                            <Box width="100%" mb="8px">
                                                <Typography color="#333333" fontSize="14px" fontWeight="500" mb="12px">
                                                    Card Details
                                                </Typography>
                                                <Box width="100%" sx={{
                                                    position: "relative",
                                                    "& .CbHosted": {
                                                        borderRadius: '3px',
                                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                                        padding: '14px 16px',
                                                        "&:hover, &--focus": {
                                                            borderColor: '#666666',
                                                        }
                                                    }
                                                }}>
                                                    <CardComponent
                                                        ref={cardRef}
                                                        currency="USD"
                                                        showTestCards={true}
                                                        styles={{
                                                            border: "1px solid #666666",
                                                            base: {
                                                                padding: "14px 16px",
                                                                border: "1px solid #666666",
                                                                fontSize: '16px',
                                                                fontFamily: 'Inter, sans-serif',
                                                                color: '#333333',
                                                                '::placeholder': {
                                                                    color: '#667085',
                                                                },
                                                            },
                                                            invalid: {
                                                                color: '#ff0000',
                                                            },
                                                        }}
                                                        onChange={(status: any) => {
                                                            console.log('Card status:', status);

                                                            if (status.error) {
                                                                setCardError(status.error.message);
                                                                setIsCardValid(false);
                                                            } else if (!status.complete) {
                                                                // Clear error but indicate card is not complete
                                                                setCardError(null);
                                                                setIsCardValid(false);
                                                            } else {
                                                                // Card is complete and valid
                                                                setCardError(null);
                                                                setIsCardValid(true);
                                                            }
                                                        }}
                                                        onReady={() => {
                                                            console.log('Card component ready');
                                                            setIsCardValid(false); // Reset validity when component is ready
                                                        }}
                                                        onBlur={() => {
                                                            console.log('Card component blurred');
                                                        }}
                                                        onFocus={() => {
                                                            console.log('Card component focused');
                                                        }}
                                                    />
                                                </Box>
                                                {cardError && (
                                                    <Typography color="error" fontSize="12px" mt={1}>
                                                        {cardError}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box display="flex" justifyContent="end" flexDirection="column">
                                                {!showPromoField ? (
                                                    <>
                                                        {appliedPromo ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    padding: '4px 8px',
                                                                    backgroundColor: '#F0FFF4',
                                                                    borderRadius: '4px',
                                                                    width: 'fit-content',
                                                                    marginLeft: 'auto'
                                                                }}
                                                            >
                                                                <Typography color="#1D9652" fontSize="14px">
                                                                    {appliedPromo.code} applied
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            null
                                                        )}
                                                    </>
                                                ) : (
                                                    <TextField
                                                        variant="outlined"
                                                        placeholder="Promo Code:"
                                                        value={promoCode}
                                                        onChange={(e) => {
                                                            setPromoCode(e.target.value);
                                                            setPromoError('');
                                                        }}
                                                        error={!!promoError}
                                                        helperText={promoError}
                                                        sx={{
                                                            width: "100%",
                                                            marginBottom: "25px",
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: '3px',
                                                                borderColor: '#C7C7C7',
                                                                background: '#ffffff',
                                                                paddingRight: '0'
                                                            },
                                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: '#333333',
                                                                border: '1px solid'
                                                            },
                                                            "& .MuiOutlinedInput-input": {
                                                                padding: '14px 16px',
                                                                background: '#ffffff',
                                                                borderRadius: '8px'
                                                            },
                                                            "&.MuiInputBase-input::placeholder": {
                                                                color: '#333333',
                                                                fontSize: '16px',
                                                                opacity: '0.8'
                                                            },
                                                        }}
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: (
                                                                    <InputAdornment
                                                                        position="end"
                                                                        sx={{
                                                                            ".MuiTypography-root": {
                                                                                color: "#333333",
                                                                                fontWeight: "500",
                                                                                fontSize: "16px",
                                                                            },
                                                                            paddingRight: "10px",
                                                                        }}
                                                                    >
                                                                        <Box display="flex" justifyContent="center" alignItems="center">
                                                                            <Box
                                                                                sx={{
                                                                                    background: theme.palette.primary.main,
                                                                                    borderRadius: '4px',
                                                                                    padding: '4px 10px',
                                                                                    marginRight: '4px',
                                                                                    minWidth: '57px',
                                                                                    fontSize: '14px',
                                                                                    fontWeight: '500',
                                                                                    color: '#fff',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                onClick={() => validateCoupon()}
                                                                            >
                                                                                Apply
                                                                            </Box>
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                width="17"
                                                                                height="16"
                                                                                viewBox="0 0 17 16"
                                                                                fill="none"
                                                                                style={{ cursor: 'pointer' }}
                                                                                onClick={() => {
                                                                                    setShowPromoField(false);
                                                                                    setPromoError('');
                                                                                }}
                                                                            >
                                                                                <path d="M8.07324 1C4.17324 1 1.07324 4.1 1.07324 8C1.07324 11.9 4.17324 15 8.07324 15C11.9732 15 15.0732 11.9 15.0732 8C15.0732 4.1 11.9732 1 8.07324 1ZM10.7732 11.5L8.07324 8.8L5.37324 11.5L4.57324 10.7L7.27324 8L4.57324 5.3L5.37324 4.5L8.07324 7.2L10.7732 4.5L11.5732 5.3L8.87324 8L11.5732 10.7L10.7732 11.5Z" fill="#A3A3A3" />
                                                                            </svg>
                                                                        </Box>
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </>
                                    <Box sx={{
                                        flex: 1, position: 'relative', '@media (max-width: 768px)': {
                                            width: '100%',
                                        }
                                    }} display="flex" flexDirection="column" justifyContent="space-between">
                                        <Box>
                                            <Box mb="8px">
                                                <Typography color='#667085' fontSize='15px' marginBottom="8px">Your Custom URL</Typography>
                                                <TextField
                                                    variant="outlined"
                                                    placeholder="https://your-brand.easywebinar.live"
                                                    value={formData.subdomain || ''}
                                                    onChange={(e) => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            subdomain: e.target.value.toLowerCase()
                                                        }));
                                                        // Clear error when user starts typing
                                                        setFormErrors(prev => ({
                                                            ...prev,
                                                            subdomain: ''
                                                        }));
                                                    }}
                                                    error={!!formErrors.subdomain}
                                                    helperText={formErrors.subdomain}
                                                    sx={{
                                                        width: "100%",
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: '3px',
                                                            borderColor: '#C7C7C7',
                                                            background: '#ffffff',
                                                            paddingRight: '0'
                                                        },

                                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: '#333333',
                                                            border: '1px solid'
                                                        },

                                                        "& .MuiOutlinedInput-input": {
                                                            padding: '11px 14px',
                                                            background: '#ffffff',
                                                            borderRadius: '8px'
                                                        },
                                                        "&.MuiInputBase-input::placeholder": {
                                                            color: '#333333',
                                                            fontSize: '16px',
                                                            opacity: '0.8'
                                                        },
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            endAdornment: (
                                                                <InputAdornment
                                                                    position="end"
                                                                    sx={{
                                                                        ".MuiTypography-root": {
                                                                            color: "#333333",
                                                                            fontWeight: "500",
                                                                            fontSize: "16px",
                                                                        },

                                                                        paddingRight: "19px",
                                                                    }}
                                                                >
                                                                    .easywebinar.live
                                                                </InputAdornment>
                                                            ),
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            {domainSuggestions.length > 0 && (
                                                <Box mt={1} mb={2}>
                                                    <Typography color="primary" fontSize="12px" mb={1}>
                                                        Available alternatives:
                                                    </Typography>
                                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                                        {domainSuggestions.map((suggestion, index) => (
                                                            <Button
                                                                key={index}
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        subdomain: suggestion
                                                                    }));
                                                                    setFormErrors(prev => ({
                                                                        ...prev,
                                                                        subdomain: ''
                                                                    }));
                                                                    setDomainSuggestions([]);
                                                                }}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    borderColor: '#D0D5DD',
                                                                    color: '#344054',
                                                                    padding: '2px 8px',
                                                                    minHeight: '24px',
                                                                    fontSize: '12px',
                                                                    '&:hover': {
                                                                        borderColor: '#667085',
                                                                        backgroundColor: '#F9FAFB'
                                                                    }
                                                                }}
                                                            >
                                                                {suggestion}
                                                            </Button>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            {!planId && <Box borderRadius="8px" bgcolor="#EDF6FF" p="24px 12px" color="#5A5475" >
                                                <Typography variant="body2" fontSize="min(calc(14px + (16 - 14) * (100vw - 320px) / (1920 - 320)), 16px)">URL Guidelines:</Typography>
                                                <Box>
                                                    <Box component="ul" sx={{ "& li": { listStyle: "disc", fontSize: "min(calc(14px + (16 - 14) * (100vw - 320px) / (1920 - 320)), 16px)" } }} pl="20px" mt="24px" lineHeight="1.5">
                                                        <Box component="li">
                                                            Use lowercase letters, numbers and hyphens only
                                                        </Box>
                                                        <Box component="li">
                                                            3-30 characters long
                                                        </Box>
                                                        <Box component="li">
                                                            No spaces or special characters
                                                        </Box>
                                                        <Box component="li">
                                                            Cannot start or end with a hyphen
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>}
                                            <Box display="flex" gap={{ xs: "13px", sm: "119px" }} justifyContent="center" alignItems="center" mt={{ xs: "20px", md: '42px' }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleGetStarted}
                                                    className="x-large-btn"
                                                    disabled={isLoading || (!isCardValid)}
                                                    disableElevation
                                                    fullWidth
                                                    sx={{
                                                        textTransform: "capitalize",
                                                        px: "44px",
                                                        height: "47px!important",
                                                        fontSize: "18px",
                                                        bgcolor: "#0382F7 !important",
                                                        "&:hover": { bgcolor: "#006dd1 !important" },
                                                        "&.Mui-disabled": {
                                                            bgcolor: "#cccccc !important",
                                                            color: "#666666 !important"
                                                        }
                                                    }}
                                                >
                                                    {isLoading ? (
                                                        <CircularProgress size={24} sx={{ color: 'white' }} />
                                                    ) : !isCardValid ? (
                                                        'Please complete card details'
                                                    ) : (
                                                        'Start your subscription'
                                                    )}
                                                </Button>
                                            </Box>
                                            
                                            {/* Lock in Annual Button */}
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Bolt sx={{ color: '#178EFC' }} />}
                                                    onClick={handleUpgradeClick}
                                                    fullWidth
                                                    sx={{
                                                        bgcolor: '#EDF6FF',
                                                        border: '1px solid #B3E5FC',
                                                        borderRadius: '8px',
                                                        color: '#178EFC',
                                                        textTransform: 'none',
                                                        fontSize: { xs: '14px', md: '16px' },
                                                        fontWeight: 600,
                                                        py: 1.5,
                                                        px: 2,
                                                        '&:hover': {
                                                            bgcolor: '#E0F2FE',
                                                            borderColor: '#178EFC',
                                                        }
                                                    }}
                                                >
                                                    Lock in ${currentPlanConfig ? currentPlanConfig.yearlyDiscountedPricePerMonth : '58'}/month with annual. Save ${currentPlanConfig ? currentPlanConfig.yearlyPrice : '696'}/year.
                                                </Button>
                                            </Box>
                                            
                                        </Box>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                mt: 2,
                                                mb: 2
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#374151',
                                                    fontSize: '10px',
                                                    fontWeight: 400,
                                                    lineHeight: 1.5,
                                                    mt: 2,
                                                }}
                                            >
                                                By completing this step, you agree to our <a href="https://easywebinar.com/terms-conditions/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit !important", textDecoration: "underline" }}>Terms of Service</a> and <a href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit !important", textDecoration: "underline" }}>Privacy Policy</a>
                                                {noTrial ? '' : ' and your 7-day free trial begins.'}
                                            </Typography>
                                        </Box>

                                        {/* G2 Badges Section - Moved to Right Side */}
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                mt: 4,
                                                px: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                                                    gap: { xs: 2, md: 3 },
                                                    maxWidth: '100%',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <img
                                                    src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-badges-group5.png"
                                                    srcSet="
                                                        https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-badges-group5.png 1x,
                                                        https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-badges-group5-2x.png 2x,
                                                        https://easywebinar-site.easywebinar.com/images/autheticator-app-images/g2-badges-group5-3x.png 3x
                                                    "
                                                    alt="G2 Badges - High Performer, Users Love Us, High Performer Asia Pacific, Leader Small Business, Momentum Leader"
                                                    style={{
                                                        maxWidth: '500px',
                                                        height: 'auto',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </CustomTabPanel>
                            </Box>
                        </Box >
                    </Box >
                </Box >
            </Box>
            <AccountVerification
                onClose={handleVerificationDialogClose}
                open={verificationDialogOpen}
                onProceed={handleVerificationDialogProceed}
                phoneNumber=""
                email={formData.email}
                password={formData.password}
                companyId={verificationData.companyId}
                companyName={verificationData.companyName}
                infLeadSquaredId={verificationData.infLeadSquaredId}
            />
        </>
    )
}
export default SaleMonthly
